'use client';

import * as React from 'react';
import { ArrowRight, ChevronRight, Pause, Play } from 'lucide-react';
import { motion, useInView } from 'motion/react';

import {
  MONITORING_ASSESSMENTS,
  nextMonitoringAssessment,
  type MonitoringAssessment
} from './homepage-monitoring-data';

const CONTROL =
  'inline-flex min-h-11 shrink-0 items-center justify-center gap-1.5 rounded-md px-2 text-xs font-medium text-[color:var(--ink-3)] transition-colors hover:bg-[color:var(--mint)] hover:text-[color:var(--accent-darker)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent-darker)] motion-reduce:transition-none';

/** Homepage-only: the standalone capability page keeps its original scene. */
export function HomepageMonitoringPreview({
  reduced
}: {
  reduced: boolean;
}): React.JSX.Element {
  const [selected, setSelected] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [pageVisible, setPageVisible] = React.useState(true);
  const previewRef = React.useRef<HTMLDivElement>(null);
  const inView = useInView(previewRef, { amount: 0.35 });
  const assessmentId = React.useId();
  const assessment = MONITORING_ASSESSMENTS[selected]!;

  React.useEffect(() => {
    const update = (): void => setPageVisible(!document.hidden);
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  React.useEffect(() => {
    if (reduced || paused || hovered || focused || !inView || !pageVisible)
      return;
    const timer = window.setTimeout(
      () => setSelected(nextMonitoringAssessment),
      12_000
    );
    return () => window.clearTimeout(timer);
  }, [selected, reduced, paused, hovered, focused, inView, pageVisible]);

  const select = (index: number): void => {
    setPaused(true);
    setSelected(index);
  };

  return (
    <div
      ref={previewRef}
      data-homepage-monitoring
      // The enclosing capability stage is live; this reading surface must not
      // announce a whole report on each automatic rotation.
      aria-live="off"
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') setHovered(true);
      }}
      onPointerLeave={() => setHovered(false)}
    >
      <header className="border-b border-[color:var(--hairline)] pb-6">
        <p className="font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-[color:var(--ink-3)]">
          Continuous account monitoring
        </p>
        <h2 className="display-h2 mt-3 max-w-[40ch] text-balance text-[22px] leading-[1.15] text-[color:var(--ink)] md:text-[24px] lg:text-[28px]">
          Know which accounts deserve attention this week.
        </h2>
        <p className="mt-3 max-w-[65ch] text-sm leading-6 text-[color:var(--ink-3)]">
          Follow your accounts and buying groups. Get a ranked view of where to
          focus, why it matters, and the evidence behind it.
        </p>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-4">
        <div>
          <p className="text-sm font-semibold text-[color:var(--ink)]">
            Enterprise accounts
          </p>
          <p className="mt-1 text-xs leading-5 text-[color:var(--ink-3)]">
            Sep 3–9 <span aria-hidden>·</span> Weekly <span aria-hidden>·</span>{' '}
            Deep
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-3">
          <div className="flex items-center">
            {!reduced && (
              <button
                type="button"
                className={CONTROL}
                aria-label={
                  paused
                    ? 'Play monitoring preview'
                    : 'Pause monitoring preview'
                }
                onClick={() => setPaused((value) => !value)}
              >
                {paused ? (
                  <Play
                    className="size-3.5"
                    aria-hidden
                  />
                ) : (
                  <Pause
                    className="size-3.5"
                    aria-hidden
                  />
                )}
                {paused ? 'Play' : 'Pause'}
              </button>
            )}
            <button
              type="button"
              className={CONTROL}
              aria-label="Show next account assessment"
              onClick={() => select(nextMonitoringAssessment(selected))}
            >
              Next{' '}
              <ChevronRight
                className="size-3.5"
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>

      <div
        className="grid min-w-0 gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.35fr)] lg:gap-8"
        onFocusCapture={() => setFocused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null))
            setFocused(false);
        }}
      >
        <nav
          aria-label="Illustrative account rankings"
          className="min-w-0"
        >
          <p className="mb-2 text-xs text-[color:var(--ink-3)]">
            Ranked for your team <span aria-hidden>·</span> Select an account
          </p>
          <ol className="space-y-1.5">
            {MONITORING_ASSESSMENTS.map((account, index) => (
              <li key={account.account}>
                <button
                  type="button"
                  aria-label={`View ${account.account} assessment: ${account.priority}`}
                  aria-pressed={selected === index}
                  aria-controls={assessmentId}
                  onClick={() => select(index)}
                  className={`group grid min-h-16 w-full grid-cols-[1rem_minmax(0,1fr)_1rem] items-start gap-2 rounded-[10px] border p-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent-darker)] motion-reduce:transition-none sm:p-4 ${selected === index ? 'border-[color:var(--accent-darker)]/20 bg-[color:var(--mint)]' : 'border-transparent hover:bg-[color:var(--mint)]'}`}
                >
                  <span className="pt-0.5 font-mono text-xs tabular-nums text-[color:var(--ink-3)]">
                    {index + 1}
                  </span>
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <span className="text-sm font-semibold text-[color:var(--ink)]">
                        {account.account}
                      </span>
                      <span
                        className={`text-xs ${selected === index ? 'text-[color:var(--accent-darker)]' : 'text-[color:var(--ink-3)]'}`}
                      >
                        {account.priority}
                      </span>
                    </span>
                    <span className="mt-2 hidden text-xs leading-5 text-[color:var(--ink-3)] lg:block">
                      {account.summary}
                    </span>
                  </span>
                  <ChevronRight
                    aria-hidden
                    className={`mt-0.5 size-4 ${selected === index ? 'text-[color:var(--accent-darker)]' : 'text-[color:var(--ink-3)] opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'}`}
                  />
                </button>
              </li>
            ))}
          </ol>
          <p className="mt-4 hidden max-w-[32ch] px-4 text-xs leading-5 text-[color:var(--ink-3)] lg:block">
            Company developments and committee activity, assessed together.
          </p>
        </nav>

        <div
          id={assessmentId}
          className="min-w-0 border-t border-[color:var(--hairline)] pt-5 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0"
        >
          <motion.div
            key={assessment.account}
            initial={reduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: reduced ? 0 : 0.2 }}
          >
            <MonitoringAssessmentContent assessment={assessment} />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export function MonitoringAssessmentContent({
  assessment
}: {
  assessment: MonitoringAssessment;
}): React.JSX.Element {
  return (
    <article
      aria-label={`${assessment.account} assessment`}
      className="min-w-0 text-[color:var(--ink)]"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold tracking-[-0.02em]">
          {assessment.account}
        </h3>
        <span className="rounded-md bg-[color:var(--mint)] px-2.5 py-1 text-xs font-medium text-[color:var(--accent-darker)]">
          {assessment.priority}
        </span>
      </div>
      <h4 className="mt-5 text-xs font-semibold text-[color:var(--ink-3)]">
        Why now
      </h4>
      <p className="mt-1.5 max-w-[32ch] text-xl font-semibold leading-[1.3] tracking-[-0.025em]">
        {assessment.headline}
      </p>
      <p className="mt-2 max-w-[62ch] text-sm leading-6 text-[color:var(--ink-3)]">
        {assessment.reasoning}
      </p>

      <h4 className="mt-5 text-xs font-semibold text-[color:var(--ink-3)]">
        Supporting evidence
      </h4>
      <ul className="mt-1 divide-y divide-[color:var(--hairline)]">
        {assessment.evidence.map((evidence) => (
          <li
            key={evidence.kind}
            className="py-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-[color:var(--ink-3)]">
              <span className="font-medium">
                {evidence.kind} <span aria-hidden>·</span> {evidence.source}
              </span>
              <time
                dateTime={evidence.dateTime}
                className="shrink-0 tabular-nums"
              >
                {evidence.date}
              </time>
            </div>
            <p className="mt-1.5 text-[13px] leading-5">{evidence.finding}</p>
          </li>
        ))}
      </ul>

      <div className="mt-3 rounded-[10px] bg-[color:var(--mint)] p-4 text-[color:var(--accent-darker)]">
        <h4 className="flex items-center gap-2 text-xs font-semibold">
          <ArrowRight
            className="size-3.5"
            aria-hidden
          />
          Suggested next step
        </h4>
        <p className="mt-2 text-sm leading-6">{assessment.nextStep}</p>
      </div>
      <p className="mt-3 text-xs leading-5 text-[color:var(--ink-3)]">
        {assessment.caveat}
      </p>
    </article>
  );
}
